import { useCallback, useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/pro-solid-svg-icons";
import { v4 as uuidv4 } from 'uuid';

import { addMarker, groupedUndo, setSpaceMarker, updateMarker, SpaceType, useAppDispatch } from "../../../store";

export function MarkerUploader({ space, onUploadComplete }: { space: SpaceType, onUploadComplete: () => void }) {
  const dispatch = useAppDispatch();

  const acceptedTypes = useMemo(() => [
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/webp',
    'image/svg+xml'
  ], []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const uploadedFiles = [...e.target.files];

    uploadedFiles.filter((f) => acceptedTypes.includes(f.type)).forEach((file) => {
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const { result } = e.target!;
        if (!result) return;

        const marker = {
          id: uuidv4(),
          base64: result.toString(),
          filename: file.name,
          size: '57x57',
        };
        groupedUndo.batch(() => {
          dispatch(addMarker(marker));
          dispatch(setSpaceMarker({ spaceId: space.id, markerId: marker.id }));
        });

        // Fix width to keep aspect ratio for new uploaded markers
        const image = new Image();
        image.onload = (i: any) => {
          if (!i.target) return;
          // Calculate new image aspect ratio
          const aspectRatio = i.target.width / i.target.height;
          if (aspectRatio) {
            // Keep height but change width for aspect ratio
            const width = Math.round(57 * aspectRatio);
            dispatch(updateMarker({...marker, size: `${width}x57` }));
          }
        };
        image.src = marker.base64!;

        onUploadComplete();
      };
      fileReader.readAsDataURL(file);
    });
  }, [dispatch, space.id, onUploadComplete, acceptedTypes]);

  return (
    <li
      role='option'
      aria-selected={false}
      title="Upload marker (svg, png, jpg, webp)"
      className="marker-uploader"
    >
      <label>
        <FontAwesomeIcon icon={faUpload} size="3x" color="white" />
        <input
          type="file"
          accept={ acceptedTypes.join(', ') }
          onChange={handleFileChange}
          style={{ position: 'absolute', visibility: 'hidden' }}
        />
      </label>
    </li>
  );
}
